import { useOptionalSidePanelPresentation } from 'designSystem/components/sidePanelContext'
import type { DocumentTableRow } from 'features/knowledgeDocumentViewer'
import { DocumentResultCard } from 'features/knowledgeDocumentViewer/components/advancedSearch/components/documentsCardView/DocumentResultCard'
import {
  type DocumentResultCardItem,
  resolveDocumentResultCardDisplay,
  revisionOptionToDocument,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/components/documentsCardView/documentResultCardItems'
import { useAdvancedSearchUX } from 'features/knowledgeDocumentViewer/components/advancedSearch/contexts/AdvancedSearchUXContext'
import type { DrawingRevisionOption } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/documentSearchApiSchemas'
import { revisionOptionPageNum } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/drawingRevisionOptions'
import { useSearchActions } from 'features/knowledgeDocumentViewer/contexts/SearchActionsContext'
import { useCallback } from 'react'

import type { PreviewDocumentHandler } from './previewDocumentHandler'

interface AgenticInlineDocumentResultCardProps {
  documentRowsById: Map<string, DocumentTableRow>
  item: DocumentResultCardItem
  onPreviewDocument: PreviewDocumentHandler
  onRevisionChange: (cardKey: string, option: DrawingRevisionOption) => void
  selectedRevision?: DrawingRevisionOption
}

export function AgenticInlineDocumentResultCard({
  documentRowsById,
  item,
  onPreviewDocument,
  onRevisionChange,
  selectedRevision,
}: AgenticInlineDocumentResultCardProps) {
  const sidePanel = useOptionalSidePanelPresentation()
  const { searchState } = useAdvancedSearchUX()
  const { onAddRecentDocument, onOpenNewTab } = useSearchActions()
  const display = resolveDocumentResultCardDisplay({
    documentRowsById,
    item,
    selectedRevision,
  })

  const openDocument = useCallback(
    (document: DocumentTableRow, pageNumber: number, openInNewTab: boolean) => {
      onAddRecentDocument(document)
      if (openInNewTab) {
        onOpenNewTab({
          documentId: document.id,
          pageNumber,
          searchOriginQuery: searchState.query,
        })
        return
      }
      onPreviewDocument(document, pageNumber)
    },
    [onAddRecentDocument, onOpenNewTab, onPreviewDocument, searchState.query]
  )

  const handleClick = useCallback(() => {
    openDocument(display.document, display.pageNumber, false)
  }, [display.document, display.pageNumber, openDocument])

  const handleOpenInNewTab = useCallback(() => {
    openDocument(display.document, display.pageNumber, true)
  }, [display.document, display.pageNumber, openDocument])

  const handlePointerDownCapture = useCallback(() => {
    sidePanel?.keepOpenOnNextOutsideClick()
  }, [sidePanel])

  const handleRevisionChange = useCallback(
    (option: DrawingRevisionOption) => {
      onRevisionChange(item.key, option)
      const revisionDocument = revisionOptionToDocument(
        item.document,
        option,
        documentRowsById.get(option.document_id)
      )
      const pageNumber =
        item.kind === 'drawingPage'
          ? revisionOptionPageNum(option, item.pageNum)
          : display.pageNumber
      openDocument(revisionDocument, pageNumber, false)
    },
    [display.pageNumber, documentRowsById, item, onRevisionChange, openDocument]
  )

  return (
    <DocumentResultCard
      document={display.document}
      drawingNumber={display.drawingNumber}
      isCompact
      isPageCard={display.isPageCard}
      onClick={handleClick}
      onDoubleClick={handleOpenInNewTab}
      onOpenInNewTab={handleOpenInNewTab}
      onPointerDownCapture={handlePointerDownCapture}
      onRevisionChange={handleRevisionChange}
      pageNumber={display.pageNumber}
      revisionCode={display.revisionCode}
      revisionOptions={display.revisionOptions}
      revisionStackingFailed={display.revisionStackingFailed}
      selectedRevisionKey={display.selectedRevisionKey}
      sheetNumber={display.sheetNumber}
    />
  )
}
